import { ReactNode } from "react";

import Box from "@mui/material/Box";

import { popCallerName, popCallerNumber } from "../incoming-call-pop.slice";
import { isColleagueLine } from "../phone-match.util";
import useUnknownCallerActions from "../unknown-caller/use-unknown-caller-actions.hook";
import {
  callCardSx,
  softphoneDialogSx
} from "./dialstack-softphone-widget.styles";
import IncomingCallUnknownCallerCard, {
  UnknownCaller
} from "./incoming-call-unknown-caller-card.component";
import type { DeviceCall } from "./use-device-call.hook";

export const DEVICE_CALL_LIVE_LABEL = "Current call";

// The unknown-caller card and its dialogs for a call taken on the handset
// (ENG-2562): the same card and actions as a browser call's, built from the
// pop alone since the SDK has no call to join. Like `useSoftphoneUnknownCaller`
// it hands back two nodes — the card goes inside the workbench, the dialogs
// outside it, where the widget's focus handlers cannot reach them. A colleague
// on the line gets no card: there is nobody to file.
export default function useDeviceCallUnknownCaller(call: DeviceCall | null): {
  card: ReactNode;
  dialogs: ReactNode;
} {
  const pop = call?.pop ?? null;
  const caller: UnknownCaller | null =
    call &&
    pop &&
    pop.patientIds.length === 0 &&
    !isColleagueLine(popCallerNumber(pop))
      ? {
          backendCallId: call.backendCallId,
          remoteNumber: popCallerNumber(pop),
          fromName: popCallerName(pop),
          contact: pop.contact,
          clinicId: pop.clinicId,
          transferredBy: pop.transfer
            ? (pop.transfer.byName ?? "a colleague")
            : null
        }
      : null;

  const { actions, dialogs } = useUnknownCallerActions({
    callId: caller?.backendCallId ?? null,
    remoteNumber: caller?.remoteNumber ?? "",
    fromName: caller?.fromName ?? null,
    clinicId: caller?.clinicId ?? null,
    dialogSx: softphoneDialogSx
  });

  const card = caller && (
    <Box sx={callCardSx}>
      <IncomingCallUnknownCallerCard
        caller={caller}
        liveLabel={DEVICE_CALL_LIVE_LABEL}
        actions={actions}
        inPop
      />
    </Box>
  );
  return { card, dialogs: caller ? dialogs : null };
}
